import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { TaxOfficeToken, CountryToken } from '@sleed/bizcommerce-api';
import { taxOfficesApi, countriesApi } from '~/lib/api/client';
import { formToToken, type TaxOfficeFormValues } from '~/lib/schemas/tax-office';

export const taxOfficeKeys = {
	all: ['tax-offices'] as const,
	list: () => [...taxOfficeKeys.all, 'list'] as const,
	countries: () => [...taxOfficeKeys.all, 'countries'] as const
};

export function useTaxOfficesList() {
	return useQuery<TaxOfficeToken[]>({
		queryKey: taxOfficeKeys.list(),
		queryFn: async () => (await taxOfficesApi.getAll()).data.items ?? []
	});
}

export function useTaxOfficeCountries() {
	return useQuery<CountryToken[]>({
		queryKey: taxOfficeKeys.countries(),
		queryFn: async () => (await countriesApi.getAll()).data.items ?? [],
		staleTime: 5 * 60 * 1000
	});
}

export function useTaxOfficeMutations() {
	const qc = useQueryClient();
	const invalidate = () => qc.invalidateQueries({ queryKey: taxOfficeKeys.list() });
	const create = useMutation({
		mutationFn: async (form: TaxOfficeFormValues) => (await taxOfficesApi.insert(formToToken(form))).data,
		onSuccess: invalidate
	});
	const update = useMutation({
		mutationFn: async (form: TaxOfficeFormValues) => (await taxOfficesApi.update(formToToken(form))).data,
		onSuccess: invalidate
	});
	const remove = useMutation({ mutationFn: async (id: number) => (await taxOfficesApi.remove(id)).data, onSuccess: invalidate });
	return { create, update, remove };
}
